/**
 * EffectLayerPanel — author a level's atmosphere visually (Scene FX v3 M3 P4.4).
 *
 * Lists the level's `effectLayers`, lets you add (kind picker) / remove / reorder /
 * enable / opacity-tune them, and edit any kind-specific knob via a per-layer JSON
 * box (full editability without 15 bespoke forms — an EffectLayer is plain data).
 * Every edit live-previews + persists onto the level via EditorSystem.setEffectLayers
 * (the editor's Save commits it; resolveLayers then prefers it over the biome preset).
 *
 * A level with no effectLayers uses its biome preset — "Seed from scene" copies the
 * live mounted preset in as a starting point.
 */
import React, { useCallback, useEffect, useState } from 'react';
import type { EditorSystem } from '../../../systems/editor/EditorSystem';
import type { EffectLayer, EffectKind } from '../../../../config/atmosphere/effectLayer.types';
import {
  AVAILABLE_EFFECT_KINDS, addEffectLayer, removeEffectLayer, moveEffectLayer,
  updateEffectLayer, ensureEffectLayerIds,
} from '../../../effects/atmosphere/effectLayerOps';
import { ATMOSPHERE_PRESETS } from '../../../../config/atmosphere/presets.config';
import type { SceneMeta } from '../../../../shared/SceneTypes';
import { TypedKnobForm } from './TypedKnobForm';

interface Props { editorSystem: EditorSystem | null; active?: boolean }

const BIOME_NAMES = Object.keys(ATMOSPHERE_PRESETS).sort();

const KIND_ICON: Record<string, string> = {
  fireflies: '✨', godRays: '🌅', glowBlobs: '🔆', lightField: '💡', bloom: '🌸',
  weather: '🌧️', fog: '🌫️', flicker: '🕯️', vignette: '⬛', parallax: '🏔️',
  skyGradient: '🌄', celestial: '🌙', critters: '🦋', cloudBands: '☁️', colorGrade: '🎨',
};

const stop = (e: React.SyntheticEvent) => e.stopPropagation();
const stripId = (l: EffectLayer): Record<string, unknown> => {
  const { id: _id, ...rest } = l as EffectLayer & { id?: string };
  return rest;
};

export const EffectLayerPanel: React.FC<Props> = ({ editorSystem, active }) => {
  const [layers, setLayers] = useState<EffectLayer[]>([]);
  const [picking, setPicking] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');       // the expanded layer's JSON edit buffer (controlled)
  const [jsonError, setJsonError] = useState(false);
  const [rawOpenId, setRawOpenId] = useState<string | null>(null); // which layer's Advanced JSON is open
  const [scene, setScene] = useState<SceneMeta>({});

  const load = useCallback(() => {
    if (!editorSystem) return;
    setLayers(ensureEffectLayerIds(editorSystem.getEffectLayers()));
    setScene(editorSystem.getSceneMeta());
  }, [editorSystem]);
  // Reload from the level whenever the tab becomes active (covers editor-not-ready-
  // at-mount + a map switch since last view) — never clobber an in-progress edit
  // mid-session.
  useEffect(() => { if (active) load(); }, [active, load]);

  const apply = useCallback((next: EffectLayer[]) => {
    setLayers(next);
    editorSystem?.setEffectLayers(next);
  }, [editorSystem]);

  // Scene-meta edits — patch through EditorSystem (persists; sceneMood live-previews).
  const patchScene = useCallback((patch: Partial<SceneMeta>) => {
    setScene((s) => ({ ...s, ...patch }));
    editorSystem?.patchScene(patch);
  }, [editorSystem]);

  // Apply the chosen biome's preset layers onto the level (editable + live preview).
  const applyBiome = () => {
    const biome = scene.biome;
    if (!editorSystem || !biome) return;
    const next = editorSystem.applyBiomeToLayers(biome);
    if (next.length) setLayers(ensureEffectLayerIds(next));
  };

  // Anchor number input → patch (blank clears the field back to preset-derived).
  const onAnchor = (key: 'groundY' | 'horizonY' | 'skyTopY') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.trim();
    patchScene({ [key]: v === '' ? undefined : Number(v) } as Partial<SceneMeta>);
  };

  const seedFromScene = () => {
    const atmo = (globalThis as { __atmosphere?: { getActiveLayers?: () => EffectLayer[] } }).__atmosphere;
    const live = atmo?.getActiveLayers?.() ?? [];
    apply(ensureEffectLayerIds(live.map((l) => ({ ...l }))));
  };

  const add = (kind: EffectKind) => { apply(addEffectLayer(layers, kind)); setPicking(false); };

  // Expand a layer's JSON editor — seed the CONTROLLED buffer from the layer
  // (collapse if already open). Controlled (not defaultValue) so a reorder/remove
  // underneath never leaves stale text that a later apply would write back.
  const toggleExpand = (l: EffectLayer, id: string) => {
    if (expandedId === id) { setExpandedId(null); setRawOpenId(null); return; }
    setExpandedId(id);
    setRawOpenId(null);
    setDraft(JSON.stringify(stripId(l), null, 2));
    setJsonError(false);
  };
  // Advanced raw-JSON escape hatch: re-seed the buffer from the layer's CURRENT
  // state each time it's opened (typed edits may have changed it since expand).
  const toggleRaw = (l: EffectLayer, id: string) => {
    if (rawOpenId === id) { setRawOpenId(null); return; }
    setRawOpenId(id);
    setDraft(JSON.stringify(stripId(l), null, 2));
    setJsonError(false);
  };
  // Apply the JSON buffer: the box is the layer's full spec (minus id), so REPLACE
  // it (id + kind preserved). Invalid / non-object JSON flags an error and never
  // touches the layer — no silent corruption.
  const applyJson = (id: string) => {
    let parsed: unknown;
    try { parsed = JSON.parse(draft); } catch { setJsonError(true); return; }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) { setJsonError(true); return; }
    setJsonError(false);
    const layer = layers.find((l) => l.id === id);
    if (!layer) return;
    apply(layers.map((l) => (l.id === id ? ({ ...(parsed as object), id, kind: layer.kind } as EffectLayer) : l)));
  };

  if (!editorSystem) return <div style={S.empty}>Editor not ready.</div>;

  const mood = scene.sceneMood ?? 1;

  return (
    <div style={S.container}>
      {/* ── Scene: biome binding + terrain anchors + mood ───────────────── */}
      <div style={S.sceneBox}>
        <div style={S.sceneTitle}>🌍 Scene</div>
        <div style={S.sceneRow}>
          <span style={S.fieldLabel}>biome</span>
          <select style={S.select} value={scene.biome ?? ''}
            onChange={(e) => patchScene({ biome: e.target.value || undefined })}>
            <option value="">(none / id-routed)</option>
            {BIOME_NAMES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <button style={S.applyBiomeBtn} disabled={!scene.biome} onClick={applyBiome}
            title="Copy this biome's preset layers onto the level as an editable starting point">→ layers</button>
        </div>
        <div style={S.sceneHint}>
          A pinned biome drives the rendered atmosphere by name (no level-id matching). "→ layers" copies its preset in so you can tweak it.
        </div>
        <div style={S.anchorGrid}>
          {(['groundY', 'horizonY', 'skyTopY'] as const).map((k) => (
            <label key={k} style={S.anchorCell}>
              <span style={S.fieldLabel}>{k}</span>
              <input type="number" style={S.numInput} value={scene[k] ?? ''} placeholder="auto"
                onChange={onAnchor(k)} />
            </label>
          ))}
        </div>
        <div style={S.sceneRow}>
          <span style={S.fieldLabel}>mood</span>
          <input type="range" min={0} max={2} step={0.05} value={mood} style={{ flex: 1 }}
            onChange={(e) => patchScene({ sceneMood: parseFloat(e.target.value) })} />
          <span style={S.opVal}>{mood.toFixed(2)}</span>
        </div>
      </div>

      <div style={S.head}>
        <span>{layers.length} FX layer{layers.length === 1 ? '' : 's'}</span>
        <button style={S.seedBtn} onClick={seedFromScene} title="Copy the live mounted atmosphere as a starting point">Seed from scene</button>
      </div>
      <div style={S.hint}>
        {layers.length === 0
          ? 'No per-level atmosphere — the biome preset is in use. Add layers (or seed) to override + persist on this level.'
          : 'Edits live-preview; the toolbar Save persists them onto the level.'}
      </div>

      <div style={S.list}>
        {layers.map((l, i) => {
          const id = l.id ?? `idx-${i}`;
          const on = l.enabled !== false;
          const op = (l.opacity ?? 1) as number;
          const expanded = expandedId === id;
          return (
            <div key={id} style={{ ...S.layer, opacity: on ? 1 : 0.5 }}>
              <div style={S.layerHead}>
                <input type="checkbox" checked={on} onClick={stop}
                  onChange={(e) => apply(updateEffectLayer(layers, id, { enabled: e.target.checked }))} />
                <span style={S.icon}>{KIND_ICON[l.kind] ?? '🌫️'}</span>
                <span style={S.kind} onClick={() => toggleExpand(l, id)} title="Edit knobs">{l.kind}</span>
                <span style={{ flex: 1 }} />
                <button style={S.iconBtn} disabled={i === 0} onClick={(e) => { stop(e); apply(moveEffectLayer(layers, id, -1)); }} title="Move up">⬆</button>
                <button style={S.iconBtn} disabled={i === layers.length - 1} onClick={(e) => { stop(e); apply(moveEffectLayer(layers, id, 1)); }} title="Move down">⬇</button>
                <button style={S.delBtn} onClick={(e) => { stop(e); apply(removeEffectLayer(layers, id)); }} title="Remove">🗑</button>
              </div>
              <div style={S.opacityRow}>
                <span style={S.opLabel}>opacity</span>
                <input type="range" min={0} max={1} step={0.05} value={op} onClick={stop}
                  onChange={(e) => apply(updateEffectLayer(layers, id, { opacity: parseFloat(e.target.value) }))} style={{ flex: 1 }} />
                <span style={S.opVal}>{op.toFixed(2)}</span>
              </div>
              {expanded && (
                <div onClick={stop}>
                  <TypedKnobForm layer={l} onPatch={(patch) => apply(updateEffectLayer(layers, id, patch))} />
                  <button style={S.rawToggle} onClick={() => toggleRaw(l, id)}>
                    {rawOpenId === id ? '▾ Advanced (raw JSON)' : '▸ Advanced (raw JSON)'}
                  </button>
                  {rawOpenId === id && (
                    <>
                      <textarea style={{ ...S.json, borderColor: jsonError ? '#e74c3c' : '#456' }} spellCheck={false}
                        value={draft} onChange={(e) => setDraft(e.target.value)}
                        title="Full layer spec; click Apply JSON to replace" />
                      <div style={S.jsonFoot}>
                        {jsonError && <span style={S.jsonErr}>invalid JSON — not applied</span>}
                        <span style={{ flex: 1 }} />
                        <button style={S.applyBtn} onClick={() => applyJson(id)}>Apply JSON</button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {picking ? (
        <div style={S.picker}>
          {AVAILABLE_EFFECT_KINDS.map((k) => (
            <button key={k} style={S.pickBtn} onClick={() => add(k)}>{KIND_ICON[k] ?? '🌫️'} {k}</button>
          ))}
          <button style={S.cancelBtn} onClick={() => setPicking(false)}>cancel</button>
        </div>
      ) : (
        <button style={S.addBtn} onClick={() => setPicking(true)}>➕ Add layer</button>
      )}
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: 8, padding: 12, height: '100%', overflowY: 'auto', color: 'white', fontSize: 12 },
  empty: { padding: 16, color: '#888', fontSize: 12 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  hint: { fontSize: 11, color: '#9aa', lineHeight: 1.4 },
  sceneBox: { background: '#243140', borderRadius: 6, padding: 10, display: 'flex', flexDirection: 'column', gap: 7, border: '1px solid #38516b' },
  sceneTitle: { fontWeight: 'bold', fontSize: 12, color: '#cde' },
  sceneRow: { display: 'flex', alignItems: 'center', gap: 6 },
  sceneHint: { fontSize: 10, color: '#8a9', lineHeight: 1.4 },
  fieldLabel: { color: '#9aa', fontSize: 10, minWidth: 44 },
  select: { flex: 1, background: '#1d2733', color: '#cfe', border: '1px solid #456', borderRadius: 4, fontSize: 11, padding: '3px 4px' },
  applyBiomeBtn: { background: 'rgba(80,140,200,0.25)', border: '1px solid #468', borderRadius: 4, color: '#cdf', fontSize: 11, cursor: 'pointer', padding: '3px 8px' },
  anchorGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 },
  anchorCell: { display: 'flex', flexDirection: 'column', gap: 2 },
  numInput: { width: '100%', background: '#1d2733', color: '#cfe', border: '1px solid #456', borderRadius: 4, fontSize: 11, padding: '3px 4px', boxSizing: 'border-box' },
  seedBtn: { background: 'rgba(80,140,200,0.25)', border: '1px solid #468', borderRadius: 4, color: '#cdf', fontSize: 11, cursor: 'pointer', padding: '3px 8px' },
  list: { display: 'flex', flexDirection: 'column', gap: 6 },
  layer: { background: '#2c3e50', borderRadius: 6, padding: 8, borderLeft: '3px solid #46607a' },
  layerHead: { display: 'flex', alignItems: 'center', gap: 6 },
  icon: { fontSize: 14 },
  kind: { fontWeight: 'bold', cursor: 'pointer' },
  iconBtn: { width: 22, height: 22, background: '#3498db', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 },
  delBtn: { width: 22, height: 22, background: '#e74c3c', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11 },
  opacityRow: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 },
  opLabel: { color: '#9aa', fontSize: 10, minWidth: 44 },
  opVal: { color: '#ddd', fontSize: 10, minWidth: 28, textAlign: 'right' },
  rawToggle: { marginTop: 6, background: 'transparent', border: 'none', color: '#89a', fontSize: 10, cursor: 'pointer', padding: '2px 0', textAlign: 'left' },
  json: { width: '100%', marginTop: 6, minHeight: 110, background: '#1d2733', color: '#cfe', border: '1px solid #456', borderRadius: 4, fontFamily: 'monospace', fontSize: 11, padding: 6, boxSizing: 'border-box', resize: 'vertical' },
  jsonFoot: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 },
  jsonErr: { color: '#e88', fontSize: 10 },
  applyBtn: { background: 'rgba(80,160,120,0.3)', border: '1px solid #4a7', borderRadius: 4, color: '#cfe', fontSize: 11, cursor: 'pointer', padding: '3px 10px' },
  addBtn: { padding: 10, background: '#27ae60', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 'bold' },
  picker: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 },
  pickBtn: { padding: '6px 4px', background: '#34495e', color: 'white', border: '1px solid #46607a', borderRadius: 4, cursor: 'pointer', fontSize: 11, textAlign: 'left' },
  cancelBtn: { gridColumn: '1 / -1', padding: 6, background: 'transparent', color: '#9aa', border: '1px solid #555', borderRadius: 4, cursor: 'pointer', fontSize: 11 },
};
